/**
 * API endpoints para Employees (Empleados)
 * Base URL: /api/employees/
 */

import { GET, POST, PUT, PATCH, DELETE } from "../config";

/**
 * Listar empleados
 */
export const list = async (page = 1, filters = {}) => {
  let url = `employees/employees/?page=${page}&page_size=50`;
  if (filters.department) url += `&department=${filters.department}`;
  if (filters.position) url += `&position=${filters.position}`;
  if (filters.employment_type)
    url += `&employment_type=${filters.employment_type}`;
  if (filters.employment_status)
    url += `&employment_status=${filters.employment_status}`;
  if (filters.is_active !== undefined) url += `&is_active=${filters.is_active}`;
  if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;
  const response = await GET(url);
  return response.data;
};

export const get = async (id) => {
  const response = await GET(`employees/employees/${id}/`);
  return response.data;
};

export const create = async (data) => {
  const response = await POST("employees/employees/", data);
  return response.data;
};

export const update = async (id, data) => {
  const response = await PUT(`employees/employees/${id}/`, data);
  return response.data;
};

export const partialUpdate = async (id, data) => {
  const response = await PATCH(`employees/employees/${id}/`, data);
  return response.data;
};

export const remove = async (id) => {
  const response = await DELETE(`employees/employees/${id}/`);
  return response.data;
};

/**
 * Obtener usuarios disponibles para vincular como empleados
 * @param {number} branchId - ID de la sucursal (opcional, usa header por defecto)
 */
export const getAvailableUsers = async (branchId = null) => {
  let url = "employees/employees/available_users/";
  if (branchId) {
    url += `?branch_id=${branchId}`;
  }
  const response = await GET(url);
  return response.data;
};

// =============================================================================
// DEPARTMENTS API
// =============================================================================

export const getDepartments = async (filters = {}) => {
  let url = "employees/departments/?page_size=100";
  if (filters.is_active !== undefined) url += `&is_active=${filters.is_active}`;
  if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;
  const response = await GET(url);
  return response.data;
};

export const getDepartment = async (id) => {
  const response = await GET(`employees/departments/${id}/`);
  return response.data;
};

export const createDepartment = async (data) => {
  const response = await POST("employees/departments/", data);
  return response.data;
};

export const updateDepartment = async (id, data) => {
  const response = await PUT(`employees/departments/${id}/`, data);
  return response.data;
};

export const deleteDepartment = async (id) => {
  const response = await DELETE(`employees/departments/${id}/`);
  return response.data;
};

// =============================================================================
// POSITIONS API
// =============================================================================

export const getPositions = async (filters = {}) => {
  let url = "employees/positions/?page_size=100";
  if (filters.department) url += `&department=${filters.department}`;
  if (filters.is_active !== undefined) url += `&is_active=${filters.is_active}`;
  if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;
  const response = await GET(url);
  return response.data;
};

export const getPosition = async (id) => {
  const response = await GET(`employees/positions/${id}/`);
  return response.data;
};

export const createPosition = async (data) => {
  const response = await POST("employees/positions/", data);
  return response.data;
};

export const updatePosition = async (id, data) => {
  const response = await PUT(`employees/positions/${id}/`, data);
  return response.data;
};

export const deletePosition = async (id) => {
  const response = await DELETE(`employees/positions/${id}/`);
  return response.data;
};

// =============================================================================
// UTILIDADES
// =============================================================================

export const getUFValue = async () => {
  const response = await GET("employees/uf-value/");
  return response.data;
};

// =============================================================================
// PAYROLL API (NÓMINA)
// =============================================================================

/**
 * Listar períodos de nómina
 */
export const listPayrollPeriods = async (params = {}) => {
  const response = await GET("employees/payroll-periods/", { params });
  return response.data;
};

/**
 * Obtener detalle de un período de nómina
 */
export const getPayrollPeriod = async (id) => {
  const response = await GET(`employees/payroll-periods/${id}/`);
  return response.data;
};

/**
 * Crear período de nómina
 */
export const createPayrollPeriod = async (data) => {
  const response = await POST("employees/payroll-periods/", data);
  return response.data;
};

/**
 * Calcular nómina para un período
 */
export const calculatePayroll = async (periodId) => {
  const response = await POST(`employees/payroll-periods/${periodId}/calculate_payroll/`);
  return response.data;
};

/**
 * Obtener resumen de un período de nómina
 */
export const getPayrollSummary = async (periodId) => {
  const response = await GET(`employees/payroll-periods/${periodId}/summary/`);
  return response.data;
};

/**
 * Descargar nómina en formato PDF
 */
export const downloadPayrollPDF = async (periodId) => {
  const response = await GET(`employees/payroll-periods/${periodId}/download/pdf/`, {
    responseType: 'blob'
  });
  return response;
};

/**
 * Descargar nómina en formato Excel
 */
export const downloadPayrollExcel = async (periodId) => {
  const response = await GET(`employees/payroll-periods/${periodId}/download/excel/`, {
    responseType: 'blob'
  });
  return response;
};

/**
 * Listar entradas de nómina
 */
export const listPayrollEntries = async (params = {}) => {
  const response = await GET("employees/payroll-entries/", { params });
  return response.data;
};

/**
 * Listar pagos de nómina
 */
export const listPayrollPayments = async (params = {}) => {
  const response = await GET("employees/payroll-payments/", { params });
  return response.data;
};

const employeesApi = {
  // Empleados
  list,
  get,
  create,
  update,
  partialUpdate,
  remove,
  getAvailableUsers,
  // Departamentos
  getDepartments,
  getDepartment,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  // Cargos
  getPositions,
  getPosition,
  createPosition,
  updatePosition,
  deletePosition,
  // Nómina
  payroll: {
    listPeriods: listPayrollPeriods,
    getPeriod: getPayrollPeriod,
    createPeriod: createPayrollPeriod,
    calculate: calculatePayroll,
    getSummary: getPayrollSummary,
    downloadPDF: downloadPayrollPDF,
    downloadExcel: downloadPayrollExcel,
    listEntries: listPayrollEntries,
    listPayments: listPayrollPayments,
  },
  // Utilidades
  getUFValue,
};

export default employeesApi;
